import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, ChevronRight, ChevronLeft, Check, Building2, Users, CalendarDays, CreditCard, Rocket, Upload, UserPlus, X, Mail, Power } from 'lucide-react';
import EmailAssistantGuidedSetup from './EmailAssistantGuidedSetup';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];

// Real app screenshots (demo account, example data only) with annotation callouts
const AppShot = ({ img, alt, annotations = [] }) => (
  <div className="relative rounded-2xl overflow-hidden border border-zinc-200 shadow-lg">
    <img src={`/onboarding/img/${img}.png`} alt={alt} className="w-full h-auto block" />
    {annotations.map((a, i) => (
      <span key={i} className="absolute bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1.5 rounded-full shadow-lg whitespace-nowrap ring-2 ring-white" style={a.style}>{a.label}</span>
    ))}
  </div>
);

const PlannerVisual = () => (
  <AppShot img="planner-week" alt="Work Planner week view" annotations={[
    { label: 'Your week at a glance', style: { top: '22%', left: '6%' } },
    { label: 'Jobs done & day value', style: { bottom: '5%', left: '6%' } },
  ]} />
);

const CustomersVisual = () => (
  <AppShot img="customers-list" alt="Customer list" annotations={[
    { label: 'All your customers in one place', style: { top: '25%', left: '6%' } },
    { label: 'Message, email or call in a tap', style: { top: '58%', right: '4%' } },
  ]} />
);

const PaymentsVisual = () => (
  <AppShot img="payments-connect" alt="Payment connections" annotations={[
    { label: 'Take card payments', style: { top: '38%', left: '6%' } },
    { label: 'Direct debit too', style: { bottom: '20%', left: '6%' } },
  ]} />
);

const DoneVisual = () => (
  <AppShot img="planner-jobs" alt="Job list" annotations={[
    { label: 'Tap a job when it\u2019s done \u2713', style: { top: '6%', left: '6%' } },
  ]} />
);

const OnboardingWizard = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [workingDays, setWorkingDays] = useState(['mon','tue','wed','thu','fri']);
  const [saving, setSaving] = useState(false);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const audioRef = useRef(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}`,
    'Content-Type': 'application/json'
  }), []);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch(`${API_URL}/api/settings`, { headers: authHeaders() });
        if (!r.ok) return;
        const d = await r.json();
        if (d.onboarding_completed === false) {
          setBusinessName(d.company_name || '');
          setBusinessPhone(d.business_phone || '');
          setWorkingDays(d.working_days || ['mon','tue','wed','thu','fri']);
          setOpen(true);
        }
      } catch (e) { /* ignore */ }
    };
    check();
    const reopen = () => { setStep(0); setOpen(true); };
    window.addEventListener('sf-open-onboarding', reopen);
    return () => window.removeEventListener('sf-open-onboarding', reopen);
  }, [authHeaders]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(false);
  };

  const toggleAudio = (name) => {
    if (playing) { stopAudio(); return; }
    const a = new Audio(`/onboarding/audio/${name}.mp3?v=2`);
    audioRef.current = a;
    a.onended = () => setPlaying(false);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const save = async (extra = {}) => {
    try {
      await fetch(`${API_URL}/api/settings`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(extra) });
    } catch (e) { /* ignore */ }
  };

  const finish = async (navigateTo) => {
    setSaving(true);
    stopAudio();
    await save({ onboarding_completed: true });
    setSaving(false);
    setOpen(false);
    window.dispatchEvent(new CustomEvent('sf-onboarding-complete'));
    if (navigateTo && onNavigate) onNavigate(navigateTo);
  };

  const next = () => { stopAudio(); setStep(s => s + 1); };
  const back = () => { stopAudio(); setStep(s => Math.max(0, s - 1)); };

  if (!open) return null;

  const steps = [
    {
      audio: 'welcome', icon: Rocket, title: "Welcome to StreakFree",
      body: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600 leading-relaxed">Let's get you set up in about 10 minutes. We'll sort your business details, customers, working days and payments — then you're cleaning.</p>
          <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-left" data-testid="onboarding-practice-mode-note">
            <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Power size={14} className="text-black" strokeWidth={2.5} />
            </div>
            <p className="text-xs text-amber-900 leading-relaxed"><strong>You're in practice mode.</strong> Nothing you do emails, invoices or charges anyone — import your round, move customers about and mark jobs done freely. When you're ready, tap the amber power button at the top of the Work Planner to go LIVE.</p>
          </div>
          <PlannerVisual />
        </div>
      )
    },
    {
      audio: 'business', icon: Building2, title: "Your business",
      body: (
        <div className="space-y-3 text-left">
          <div>
            <label className="text-xs font-semibold text-zinc-600">Business name</label>
            <input value={businessName} onChange={e=>setBusinessName(e.target.value)} placeholder="e.g. Shiny Windows Ltd"
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-emerald-500" data-testid="onboarding-business-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-600">Your mobile</label>
            <input value={businessPhone} onChange={e=>setBusinessPhone(e.target.value)} placeholder="07…" inputMode="tel"
              className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-emerald-500" data-testid="onboarding-business-phone" />
          </div>
          <p className="text-[11px] text-zinc-400">These appear on your invoices and messages so customers know it's you.</p>
        </div>
      ),
      onNext: () => save({ company_name: businessName || undefined, business_phone: businessPhone || undefined })
    },
    {
      audio: 'customers', icon: Users, title: "Your customers",
      body: (
        <div className="space-y-3">
          <CustomersVisual />
          <button onClick={() => finish('sync-centre')} className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-left" data-testid="onboarding-import-btn">
            <Upload size={18} className="text-emerald-600 flex-shrink-0" />
            <div><p className="text-sm font-bold text-zinc-900">Import from Squeegee / spreadsheet</p><p className="text-[11px] text-zinc-500">You'll need 2 files — Active Customers &amp; All Job Data. We'll show you exactly how to get them</p></div>
          </button>
          <button onClick={() => finish('customers')} className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-zinc-200 text-left" data-testid="onboarding-add-customer-btn">
            <UserPlus size={18} className="text-zinc-600 flex-shrink-0" />
            <div><p className="text-sm font-bold text-zinc-900">Add my first customer</p><p className="text-[11px] text-zinc-500">Starting fresh — takes 30 seconds</p></div>
          </button>
          <p className="text-[11px] text-zinc-400 text-center">Or tap Next to do this later</p>
        </div>
      )
    },
    {
      audio: 'days', icon: CalendarDays, title: "When do you work?",
      body: (
        <div className="space-y-3">
          <div className="grid grid-cols-7 gap-1.5">
            {DAYS.map(([id, label]) => (
              <button key={id}
                onClick={() => setWorkingDays(w => w.includes(id) ? w.filter(d=>d!==id) : [...w, id])}
                className={`py-3 rounded-xl text-xs font-bold transition-all ${workingDays.includes(id) ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-400'}`}
                data-testid={`onboarding-day-${id}`}
              >{label}</button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400">You can always move jobs to any day — this just shapes your week.</p>
        </div>
      ),
      onNext: () => save({ working_days: workingDays })
    },
    {
      audio: 'payments', icon: CreditCard, title: "Get paid",
      body: (
        <div className="space-y-3">
          <PaymentsVisual />
          <button onClick={() => finish('connected-apps')} className="w-full p-3 rounded-xl bg-zinc-900 text-white text-sm font-bold" data-testid="onboarding-connect-payments-btn">
            Connect payments now
          </button>
          <p className="text-[11px] text-zinc-400 text-center">Cards work the same day · direct debit takes 1–2 days to verify · or tap Next and do it later</p>
        </div>
      )
    },
    {
      audio: 'email', icon: Mail, title: "Email answered for you",
      body: (
        <div className="space-y-3">
          <div className="p-4 bg-sky-50 border border-sky-200 rounded-2xl">
            <p className="text-sm text-zinc-700 leading-relaxed">Forward your business email through StreakFree and when a customer asks <em>"when's my next clean?"</em>, we reply within a minute — always with an <strong>"on or around"</strong> date, branded as your business. Every other email is left alone.</p>
          </div>
          <button onClick={() => setShowEmailSetup(true)} className="w-full p-3 rounded-xl bg-sky-500 text-white text-sm font-bold" data-testid="onboarding-email-setup-btn">
            Set up now — 2 minute guided walkthrough
          </button>
          <p className="text-[11px] text-zinc-400 text-center">Or tap Next — it's always in Settings → Email Assistant</p>
        </div>
      )
    },
    {
      audio: 'done', icon: Check, title: "You're ready!",
      body: (
        <div className="space-y-3">
          <DoneVisual />
          <p className="text-sm text-zinc-600 leading-relaxed">Your Work Planner shows each day's jobs. Tap a job when it's done — StreakFree handles the invoice. Stuck? Ask the Help chat.</p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 text-left leading-relaxed" data-testid="onboarding-done-practice-reminder"><strong>Remember:</strong> you're still in practice mode — flip the power button at the top of the Work Planner to LIVE when you want invoices and customer messages switched on.</p>
        </div>
      )
    },
  ];

  const s = steps[step];
  const Icon = s.icon;
  const isLast = step === steps.length - 1;

  return (
    <>
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="onboarding-wizard">
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 pb-28 sm:pb-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-emerald-500' : i < step ? 'w-1.5 bg-emerald-300' : 'w-1.5 bg-zinc-200'}`} />
              ))}
            </div>
            <button onClick={() => finish()} className="text-xs font-semibold text-zinc-400 flex items-center gap-1" data-testid="onboarding-skip-btn">
              Skip setup <X size={14} />
            </button>
          </div>

          {/* Icon + audio */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0"><Icon size={22} /></div>
            <h2 className="text-lg font-extrabold text-zinc-900 flex-1">{s.title}</h2>
            <button
              onClick={() => toggleAudio(s.audio)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all ${playing ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}
              data-testid="onboarding-audio-btn"
            >
              {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? 'Pause' : 'Listen'}
            </button>
          </div>

          {s.body}

          {/* Nav */}
          <div className="flex gap-2 mt-5">
            {step > 0 && (
              <button onClick={back} className="px-4 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 flex items-center gap-1" data-testid="onboarding-back-btn">
                <ChevronLeft size={16} /> Back
              </button>
            )}
            <button
              onClick={async () => {
                if (s.onNext) await s.onNext();
                if (isLast) { await finish('work-planner'); } else { next(); }
              }}
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
              data-testid="onboarding-next-btn"
            >
              {isLast ? 'Open my planner' : 'Next'} <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
    <EmailAssistantGuidedSetup open={showEmailSetup} onClose={() => setShowEmailSetup(false)} />
    </>
  );
};

export default OnboardingWizard;
